import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { taskStatusListSchema } from "@/lib/task-status-options";

// Filters live in the URL so they survive refresh and can be shared/bookmarked.
// Off-contract values degrade to undefined instead of failing the route.
const crmTaskSearchSchema = z.object({
  status: taskStatusListSchema,
  priority: z.string().min(1).optional().catch(undefined),
  assigned_to: z.number().optional().catch(undefined),
  q: z.string().min(1).optional().catch(undefined),
});

export const Route = createFileRoute("/user/_layout/crm/tasks")({
  validateSearch: crmTaskSearchSchema,
  component: CrmMyTasksPage,
});

import { useEffect, useState, useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { MultiSelectFilter } from "@/components/crud/filters/MultiSelectFilter";
import { getTaskStatusOptions } from "@/lib/task-status-options";
import type { TaskStatus } from "@/types/task";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { useDebounce } from "@/hooks/use-debounce";
import {
  useDataTableOrdering,
  type SortDirection,
} from "@/hooks/ui/useDataTableOrdering";

import { AssigneeFilter } from "@/components/crm/AssigneeFilter";
import CrmMyTaskTable from "@/components/crm/tables/CrmMyTaskTable";
import { CrmTaskFormSheet } from "@/components/crm/forms/CrmTaskFormSheet";
import { SubtaskFormSheet } from "@/components/crud/forms/SubtaskFormSheet";
import { CompleteTaskDialog } from "@/components/crud/forms/CompleteTaskDialog";
import {
  useFetchCrmTasks,
  useCrmTaskMutations,
  type CrmTaskOrdering,
} from "@/hooks/api/crm/useCrmTasks";
import { crmKeys } from "@/hooks/api/crm/keys";
import type { CrmTask, CrmTaskPayload } from "@/types/crm";
import type { Subtask, SubtaskPayload } from "@/types/subtask";

// Shared HR lifecycle hooks (CRM tasks reach the same endpoints)
import useStartTask from "@/hooks/api/tasks/useStartTask";
import useCompleteTask from "@/hooks/api/tasks/useCompleteTask";
import useSubmitTask from "@/hooks/api/tasks/approval/useSubmitTask";
import useStartSubtask from "@/hooks/api/tasks/subtask/useStartSubtask";
import useCompleteSubtask from "@/hooks/api/tasks/subtask/useCompleteSubtask";
import useCreateSubtask from "@/hooks/api/tasks/subtask/useCreateSubtask";
import useUpdateSubtask from "@/hooks/api/tasks/subtask/useUpdateSubtask";
import useDeleteSubtask from "@/hooks/api/tasks/subtask/useDeleteSubtask";
import { useAssigneeOptions } from "@/hooks/api/crm/useAssigneeOptions";
import { useOrgSettingsStore } from "@/stores/useOrgSettingsStore";
import { useAuthStore } from "@/stores/useAuthStore";

function CrmMyTasksPage() {
  const queryClient = useQueryClient();

  // Task lifecycle/subtask actions below hit the shared HR task endpoints
  // (`/api/tasks/...`), which only know about `taskKeys` — they can't
  // invalidate the CRM tasks list this page reads from. Nudge it manually
  // whenever one of those mutations changes a row this page is showing.
  const invalidateCrmTasks = useCallback(
    () => queryClient.invalidateQueries({ queryKey: crmKeys.crmTasks.all }),
    [queryClient],
  );

  const { createCrmTask, updateCrmTask, deleteCrmTask, deferCrmTask } =
    useCrmTaskMutations();

  const startTask = useStartTask();
  const completeTask = useCompleteTask();
  const submitTask = useSubmitTask();
  const startSubtask = useStartSubtask();
  const completeSubtask = useCompleteSubtask();
  const createSubtask = useCreateSubtask();
  const updateSubtask = useUpdateSubtask();
  const deleteSubtask = useDeleteSubtask();

  const taskApprovalRequired = useOrgSettingsStore(
    (s) => s.settings?.task_approval_required ?? true
  );

  // Filters / pagination
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const navigate = useNavigate();
  const searchParams = Route.useSearch();
  const statusFilter = searchParams.status;
  const priorityFilter = searchParams.priority;
  const currentUserId = useAuthStore((s) => s.user?.user_id);
  const [assigneeCleared, setAssigneeCleared] = useState(false);
  const assignedTo = assigneeCleared ? undefined : (searchParams.assigned_to ?? currentUserId);
  const setStatusFilter = useCallback(
    (values: TaskStatus[]) =>
      navigate({
        to: ".",
        search: (prev) => ({
          ...prev,
          status: values.length > 0 ? values : undefined,
        }),
        replace: true,
      }),
    [navigate]
  );
  const setPriorityFilter = useCallback(
    (value: string | undefined) =>
      navigate({
        to: ".",
        search: (prev) => ({ ...prev, priority: value || undefined }),
        replace: true,
      }),
    [navigate]
  );
  const setAssignedTo = useCallback(
    (value: number | undefined) => {
      setAssigneeCleared(value === undefined);
      navigate({
        to: ".",
        search: (prev) => ({ ...prev, assigned_to: value }),
        replace: true,
      });
    },
    [navigate]
  );
  const [searchQuery, setSearchQuery] = useState(searchParams.q ?? "");
  const [debouncedSearch, resetDebounce] = useDebounce(searchQuery, 500);
  const [showFilters, setShowFilters] = useState(false);

  // Persist the (debounced) search text in the URL alongside the dropdown filters.
  useEffect(() => {
    navigate({
      to: ".",
      search: (prev) => ({ ...prev, q: debouncedSearch || undefined }),
      replace: true,
    });
  }, [debouncedSearch, navigate]);

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        user__first_name: SortDirection;
        title: SortDirection;
        status: SortDirection;
        priority: SortDirection;
        started_at: SortDirection;
        completed_at: SortDirection;
        due_date: SortDirection;
        created_at: SortDirection;
      },
      CrmTaskOrdering
    >({
      user__first_name: null,
      title: null,
      status: null,
      priority: null,
      started_at: null,
      completed_at: null,
      due_date: null,
      created_at: null,
    });

  // Sheets / dialogs
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [editingTask, setEditingTask] = useState<CrmTask | null>(null);

  const [isSubtaskSheetOpen, setIsSubtaskSheetOpen] = useState(false);
  const [parentTask, setParentTask] = useState<CrmTask | null>(null);
  const [editingSubtask, setEditingSubtask] = useState<Subtask | null>(null);
  const [viewingSubtask, setViewingSubtask] = useState<Subtask | null>(null);
  const [subtaskAssigneeSearch, setSubtaskAssigneeSearch] = useState("");
  const subtaskAssigneeQuery = useAssigneeOptions({
    search: subtaskAssigneeSearch,
    enabled: isSubtaskSheetOpen,
  });

  const [taskToDelete, setTaskToDelete] = useState<CrmTask | null>(null);
  const [taskToComplete, setTaskToComplete] = useState<CrmTask | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [subtaskToDelete, setSubtaskToDelete] = useState<{
    subtask: Subtask;
    taskId: number;
  } | null>(null);
  const [isSubtaskDeleteOpen, setIsSubtaskDeleteOpen] = useState(false);

  const crmTasksQuery = useFetchCrmTasks({
    page,
    page_size: pageSize,
    search: debouncedSearch || undefined,
    status_in: statusFilter?.length ? statusFilter : undefined,
    priority: priorityFilter || undefined,
    assigned_to: assignedTo ? String(assignedTo) : undefined,
    ordering: getApiOrdering(),
  });
  const crmTasks = crmTasksQuery.data?.results ?? [];
  const count = crmTasksQuery.data?.count ?? 0;

  const isMutating =
    createCrmTask.isPending ||
    updateCrmTask.isPending ||
    deleteCrmTask.isPending ||
    startTask.isPending ||
    completeTask.isPending ||
    submitTask.isPending ||
    startSubtask.isPending ||
    completeSubtask.isPending ||
    createSubtask.isPending ||
    updateSubtask.isPending ||
    deleteSubtask.isPending;
  const isLoading = crmTasksQuery.isPending || isMutating;
  const isFetching = crmTasksQuery.isFetching || isMutating;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(count / pageSize)),
    [count, pageSize]
  );

  const resetFilters = useCallback(() => {
    navigate({ to: ".", search: {}, replace: true });
    setAssigneeCleared(true);
    setSearchQuery("");
    resetDebounce();
    setOrdering({
      user__first_name: null,
      title: null,
      status: null,
      priority: null,
      started_at: null,
      completed_at: null,
      due_date: null,
      created_at: null,
    });
    setPage(1);
  }, [navigate, resetDebounce, setOrdering, setPage]);

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, priorityFilter, assignedTo, ordering, setPage]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Task CRUD
  const handleCreate = async (data: CrmTaskPayload) => {
    await createCrmTask.mutateAsync(data);
    toast.success("Task created!");
    setIsSheetOpen(false);
  };

  const handleEdit = async (id: number, data: Partial<CrmTaskPayload>) => {
    await updateCrmTask.mutateAsync({ id, payload: data });
    toast.success("Task updated!");
    setIsSheetOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!taskToDelete) return;
    try {
      await deleteCrmTask.mutateAsync({ id: taskToDelete.id });
      toast.success("Task deleted!");
      setIsDeleteDialogOpen(false);
      setTaskToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  // Task lifecycle
  const handleStartTask = async (task: CrmTask) => {
    try {
      await startTask.mutateAsync({ id: task.id });
      toast.success("Task started!");
      invalidateCrmTasks();
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  // Completion opens a dialog so the user can record what was done.
  const handleCompleteTask = (task: CrmTask) => {
    setTaskToComplete(task);
  };

  const handleCompleteConfirm = async (remarks: string) => {
    if (!taskToComplete) return;
    await completeTask.mutateAsync({
      id: taskToComplete.id,
      payload: { completion_remarks: remarks },
    });
    toast.success("Task completed!");
    invalidateCrmTasks();
    setTaskToComplete(null);
  };

  const handleSubmitTask = async (task: CrmTask) => {
    try {
      await submitTask.mutateAsync({ id: task.id });
      toast.success("Task submitted for approval!");
    } catch (err: any) {
      const msg = err?.response?.data?.detail || "";
      if (msg.toLowerCase().includes("not required")) {
        submitTask.reset();
        try {
          await startTask.mutateAsync({ id: task.id });
          toast.success("Task started!");
        } catch {
          // error toast comes from the mutation's meta.errorMessage
        }
      }
    }
    invalidateCrmTasks();
  };

  const handleDeferTask = async (task: CrmTask) => {
    try {
      await deferCrmTask.mutateAsync({ id: task.id });
      toast.success("Task deferred!");
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  // Subtask lifecycle
  const handleStartSubtask = async (subtask: Subtask, taskId: number) => {
    try {
      await startSubtask.mutateAsync({ taskId, id: subtask.id });
      invalidateCrmTasks();
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleCompleteSubtask = async (subtask: Subtask, taskId: number) => {
    try {
      await completeSubtask.mutateAsync({ taskId, id: subtask.id });
      invalidateCrmTasks();
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  // Subtask CRUD
  const handleAddSubtask = (task: CrmTask) => {
    setParentTask(task);
    setEditingSubtask(null);
    setIsSubtaskSheetOpen(true);
  };

  const handleEditSubtask = (subtask: Subtask, taskId: number) => {
    const parent = crmTasks.find((t) => t.id === taskId) ?? null;
    setParentTask(parent);
    setEditingSubtask(subtask);
    setViewingSubtask(null);
    setIsSubtaskSheetOpen(true);
  };

  const handleViewSubtask = (subtask: Subtask, taskId: number) => {
    const parent = crmTasks.find((t) => t.id === taskId) ?? null;
    setParentTask(parent);
    setEditingSubtask(subtask);
    setViewingSubtask(subtask);
    setIsSubtaskSheetOpen(true);
  };

  const handleSubtaskFormSubmit = async (data: SubtaskPayload) => {
    if (!parentTask) return;
    try {
      if (editingSubtask) {
        await updateSubtask.mutateAsync({
          taskId: parentTask.id,
          id: editingSubtask.id,
          payload: data,
        });
        toast.success("Subtask updated!");
      } else {
        await createSubtask.mutateAsync({
          taskId: parentTask.id,
          payload: data,
        });
        toast.success("Subtask created!");
      }
      setIsSubtaskSheetOpen(false);
      setEditingSubtask(null);
      invalidateCrmTasks();
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleDeleteSubtaskClick = (subtask: Subtask, taskId: number) => {
    setSubtaskToDelete({ subtask, taskId });
    setIsSubtaskDeleteOpen(true);
  };

  const handleDeleteSubtaskConfirm = async () => {
    if (!subtaskToDelete) return;
    try {
      await deleteSubtask.mutateAsync({
        taskId: subtaskToDelete.taskId,
        id: subtaskToDelete.subtask.id,
      });
      toast.success("Subtask deleted!");
      setIsSubtaskDeleteOpen(false);
      setSubtaskToDelete(null);
      invalidateCrmTasks();
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="w-full space-y-4 p-6 pt-0">
        <PageHeader
          title="My CRM Tasks"
          description="View and track all your assigned CRM tasks."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={() => {
            setEditingTask(null);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          addLabel="Add Task"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search tasks..."
          />
          <AssigneeFilter value={assignedTo} onChange={setAssignedTo} className="w-[220px]" />
          <MultiSelectFilter
            placeholder="Status"
            options={getTaskStatusOptions({
              taskApprovalRequired,
              includeDeferred: true,
            })}
            values={statusFilter ?? []}
            onChange={setStatusFilter}
          />
          <SelectFilter
            placeholder="Priority"
            options={[
              { label: "Low", value: "Low" },
              { label: "Normal", value: "Normal" },
              { label: "High", value: "High" },
              { label: "All", value: "clear" },
            ]}
            value={priorityFilter}
            onChange={(value) =>
              setPriorityFilter(value === "clear" ? undefined : value)
            }
          />
        </FiltersPanel>

        <CrmMyTaskTable
          tasks={crmTasks}
          loading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          onTitleClick={(id) =>
            navigate({ to: "/user/crm/tasks/$id", params: { id: String(id) } })
          }
          onView={(task) => {
            setEditingTask(task);
            setIsViewOnly(true);
            setIsSheetOpen(true);
          }}
          onStartTask={handleStartTask}
          onCompleteTask={handleCompleteTask}
          onSubmitTask={handleSubmitTask}
          onDeferTask={handleDeferTask}
          onStartSubtask={handleStartSubtask}
          onCompleteSubtask={handleCompleteSubtask}
          onAddSubtask={handleAddSubtask}
          onEdit={(task) => {
            setEditingTask(task);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          onEditSubtask={handleEditSubtask}
          onViewSubtask={handleViewSubtask}
          onDelete={(task) => {
            setTaskToDelete(task);
            setIsDeleteDialogOpen(true);
          }}
          onDeleteSubtask={handleDeleteSubtaskClick}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <CrmTaskFormSheet
          isOpen={isSheetOpen}
          isViewOnly={isViewOnly}
          onClose={() => setIsSheetOpen(false)}
          onCreate={handleCreate}
          onEdit={handleEdit}
          editingTask={editingTask}
          isLoading={isMutating}
          onEditClick={isViewOnly ? () => setIsViewOnly(false) : undefined}
        />

        <SubtaskFormSheet
          isOpen={isSubtaskSheetOpen}
          onClose={() => {
            setIsSubtaskSheetOpen(false);
            setEditingSubtask(null);
            setViewingSubtask(null);
          }}
          onSubmit={handleSubtaskFormSubmit}
          parentTaskTitle={parentTask?.title}
          isLoading={createSubtask.isPending || updateSubtask.isPending}
          editingSubtask={editingSubtask}
          isViewOnly={viewingSubtask != null}
          onEditClick={() => setViewingSubtask(null)}
          assigneeUsers={subtaskAssigneeQuery.users}
          assigneeUsersLoading={subtaskAssigneeQuery.isLoading}
          fetchNextAssigneePage={subtaskAssigneeQuery.fetchNextPage}
          hasMoreAssignees={subtaskAssigneeQuery.hasMore}
          onAssigneeSearchChange={setSubtaskAssigneeSearch}
        />

        <CompleteTaskDialog
          isOpen={!!taskToComplete}
          onClose={() => setTaskToComplete(null)}
          taskTitle={taskToComplete?.title}
          isLoading={completeTask.isPending}
          onConfirm={handleCompleteConfirm}
        />

        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => {
            setIsDeleteDialogOpen(false);
            setTaskToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          title="Delete Task"
          description={`Delete task "${taskToDelete?.title}"? This action cannot be undone.`}
          itemName={taskToDelete?.title}
          isLoading={deleteCrmTask.isPending}
          destructive
        />

        <DeleteConfirmDialog
          isOpen={isSubtaskDeleteOpen}
          onClose={() => {
            setIsSubtaskDeleteOpen(false);
            setSubtaskToDelete(null);
          }}
          onConfirm={handleDeleteSubtaskConfirm}
          title="Delete Subtask"
          description={`Delete subtask "${subtaskToDelete?.subtask.title}"? This action cannot be undone.`}
          itemName={subtaskToDelete?.subtask.title}
          isLoading={deleteSubtask.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
